import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ROUTES_NAMES } from '../components/Layout';
import { apiService } from '../services/api';
import { CUE_CHIP_LABEL, formatPassRate, passRateForCue, PGM_CUE_KINDS } from './pgmAuditUtils';

export default function PgmAuditListPage() {
  const navigate = useNavigate();
  const auditsQuery = useQuery({
    queryKey: ['pgm-audits'],
    queryFn: () => apiService.getPgmAudits(),
  });

  const runs = auditsQuery.data?.runs ?? [];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        PGM audit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review full-episode frame sweeps, mark incorrect passes, and curate PGM fixtures. Frames live under{' '}
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
          packages/pipelines/pgm-audit/&lt;contentId&gt;/frames/
        </Typography>
        .
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Run an audit (all cues by default):{' '}
        <Typography component="span" sx={{ fontFamily: 'monospace' }}>
          yarn visual-cues:audit-pgm-frames --content-id &lt;id&gt; --keep-video
        </Typography>
        . Re-runs reuse cached source.mp4; add{' '}
        <Typography component="span" sx={{ fontFamily: 'monospace' }}>
          --force
        </Typography>{' '}
        to re-sample, or{' '}
        <Typography component="span" sx={{ fontFamily: 'monospace' }}>
          --force-download
        </Typography>{' '}
        to fetch video again.
      </Alert>

      <Paper variant="outlined">
        {auditsQuery.isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : runs.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">
              No PGM audit runs found under packages/pipelines/pgm-audit/
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sampled</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Run type</TableCell>
                  <TableCell align="right">Frames</TableCell>
                  {PGM_CUE_KINDS.map((cue) => (
                    <TableCell key={cue} align="right">
                      {CUE_CHIP_LABEL[cue]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow
                    key={run.contentId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate(
                        ROUTES_NAMES.PGM_AUDIT_DETAIL.replace(':contentId', run.contentId),
                      )
                    }
                  >
                    <TableCell>{new Date(run.sampledAt).toLocaleString('es-CL')}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>
                        {run.title ?? run.contentId}
                      </Typography>
                    </TableCell>
                    <TableCell>{run.cue}</TableCell>
                    <TableCell align="right">{run.frameCount.toLocaleString()}</TableCell>
                    {PGM_CUE_KINDS.map((cue) => (
                      <TableCell key={cue} align="right">
                        {formatPassRate(passRateForCue(run, cue))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Typography variant="body2" sx={{ mt: 2 }}>
        <RouterLink to={ROUTES_NAMES.PODCAST_LEARNINGS_EXPLORER}>← Back to podcast learnings</RouterLink>
      </Typography>
    </Box>
  );
}
